import {
  createPlatformActionRegistry,
  type PlatformAction,
} from '@command-center/core-platform';

/**
 * First concrete Platform Action registry for the One Up internal portal.
 *
 * This is the convergence point described in Pass 5: the command palette
 * (manual `/` commands) and Alice (contextual suggestions) should both resolve
 * actions from one source so they never drift. Today the command palette reads
 * the `navigation` actions below to render its `/goto` commands. Alice's
 * contextual suggestions continue to come from the shared suggestion catalog in
 * `@command-center/core-platform`; a future pass can map suggestions <-> these
 * platform actions so a single key drives both surfaces.
 *
 * Frontend-safe: descriptors only. Any `handlerType: 'server'` action ultimately
 * delegates to a server-side `@command-center/action-sdk` action with its own
 * permission and approval checks.
 */
export const PLATFORM_ACTIONS: PlatformAction[] = [
  // --- Navigation (consumed by the command palette today) ---
  {
    key: 'nav.tasks',
    label: 'Open Tasks',
    category: 'navigation',
    scope: 'global',
    handlerType: 'frontend',
    paletteCommand: '/goto tasks',
    route: '/tasks',
    icon: 'CheckSquare',
  },
  {
    key: 'nav.clients',
    label: 'Open Clients',
    category: 'navigation',
    scope: 'global',
    handlerType: 'frontend',
    paletteCommand: '/goto clients',
    route: '/clients',
    icon: 'Building2',
  },
  {
    key: 'nav.contacts',
    label: 'Open Contacts',
    category: 'navigation',
    scope: 'global',
    handlerType: 'frontend',
    paletteCommand: '/goto contacts',
    route: '/users',
    icon: 'User',
  },
  {
    key: 'nav.websites',
    label: 'Open Websites',
    category: 'navigation',
    scope: 'global',
    handlerType: 'frontend',
    paletteCommand: '/goto websites',
    route: '/websites',
    icon: 'Globe',
  },
  {
    key: 'nav.proposals',
    label: 'Open Proposals',
    category: 'navigation',
    scope: 'global',
    handlerType: 'frontend',
    paletteCommand: '/goto proposals',
    route: '/proposals',
    icon: 'FileText',
  },

  // --- AI / Alice entry points (manual surfaces that mirror Alice) ---
  {
    key: 'ai.ask_alice',
    label: 'Ask Alice...',
    description: 'Open Alice with a conversational prompt.',
    category: 'ai',
    scope: 'global',
    handlerType: 'ai',
    icon: 'Sparkles',
  },
  {
    key: 'ai.open_composer',
    label: 'Open Composer',
    description: 'Start a guarded Alice draft session.',
    category: 'ai',
    scope: 'page',
    handlerType: 'ai',
    startsComposerSession: true,
    icon: 'FilePenLine',
  },
];

export const platformActionRegistry = createPlatformActionRegistry(PLATFORM_ACTIONS);

/** Navigation actions, used by the command palette to render `/goto` commands. */
export const NAVIGATION_PLATFORM_ACTIONS = platformActionRegistry.byCategory('navigation');
